import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Info, XCircle, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import systemAlertService from '../../services/systemAlertService';

/**
 * Global system alert banner component
 * Displays active alerts at the top of all pages
 * Users can dismiss alerts (stored in backend + localStorage backup)
 */
const SystemAlertBanner = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Get localStorage key for dismissed alerts (backup)
  const getDismissedKey = useCallback(() => {
    return user ? `dismissed_alerts_${user.id}` : null;
  }, [user]);

  // Get locally dismissed alert IDs
  const getLocalDismissed = useCallback(() => {
    const key = getDismissedKey();
    if (!key) return [];
    try {
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch {
      return [];
    }
  }, [getDismissedKey]);

  // Fetch active alerts
  const fetchAlerts = useCallback(async () => {
    if (!user) return;

    try {
      const activeAlerts = await systemAlertService.getActiveAlerts();
      // Also filter by local dismissals as backup
      const localDismissed = getLocalDismissed();
      const filtered = activeAlerts.filter(
        alert => !localDismissed.includes(alert.id)
      );
      setAlerts(filtered);
    } catch (err) {
      console.error('Error fetching system alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [user, getLocalDismissed]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Handle dismissing an alert
  const handleDismiss = async (alertId) => {
    try {
      // Call API to persist dismissal
      await systemAlertService.dismissAlert(alertId);

      // Also save to localStorage as backup
      const key = getDismissedKey();
      if (key) {
        const dismissed = getLocalDismissed();
        if (!dismissed.includes(alertId)) {
          dismissed.push(alertId);
          localStorage.setItem(key, JSON.stringify(dismissed));
        }
      }

      // Remove from state
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
    } catch (err) {
      console.error('Error dismissing alert:', err);
      // Even if API fails, remove locally
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
    }
  };

  // Don't render anything while loading or if no alerts
  if (loading || alerts.length === 0) {
    return null;
  }

  // Severity configurations matching the existing toast styles
  const severityConfig = {
    info: {
      icon: Info,
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-500',
      iconColor: 'text-primary-600',
      textColor: 'text-primary-800'
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-500',
      iconColor: 'text-yellow-600',
      textColor: 'text-yellow-800'
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-500',
      iconColor: 'text-red-600',
      textColor: 'text-red-800'
    }
  };

  return (
    <div className="w-full">
      {alerts.map(alert => {
        const config = severityConfig[alert.severity] || severityConfig.info;
        const Icon = config.icon;

        return (
          <div
            key={alert.id}
            className={`${config.bgColor} ${config.borderColor} border-b-2`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <Icon className={`h-5 w-5 ${config.iconColor} flex-shrink-0`} />
                  <p className={`ml-3 text-sm font-medium ${config.textColor}`}>
                    {alert.message}
                  </p>
                </div>
                <button
                  onClick={() => handleDismiss(alert.id)}
                  className={`ml-4 flex-shrink-0 ${config.iconColor} hover:opacity-75 transition-opacity`}
                  aria-label="Dismiss alert"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SystemAlertBanner;
